import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import '../styles/AlertsBanner.css';

const TYPE_LABELS = {
  zero_pay: '$0 Loads',
  zero_miles: '0-Mile Loads',
  low_rpm: 'Low RPM (<$1.50)',
  high_deadhead: 'High Deadhead (>40%)',
  empty_exceeds_loaded: 'Empty > Loaded Miles',
};

const TYPE_ICONS = {
  zero_pay: '\u26A0',
  zero_miles: '\u26A0',
  low_rpm: '\u26A0',
  high_deadhead: '\u26A0',
  empty_exceeds_loaded: '\u26A0',
};

const SEVERITY_ICONS = {
  critical: '\uD83D\uDD34',
  warning: '\u26A0\uFE0F',
};

export default function AlertsBanner({ selectedCompany, buildParams }) {
  const [alertData, setAlertData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedType, setExpandedType] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const res = await api.get(`/loads/${selectedCompany}/alerts?${buildParams()}`);
      setAlertData(res.data);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
      setAlertData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, buildParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    if (!alertData || !alertData.alerts || alertData.alerts.length === 0) return;
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const rows = alertData.alerts.map((a) => ({
        'Alert Type': TYPE_LABELS[a.alert_type] || a.alert_type,
        'Severity': a.severity,
        'Load ID': a.load_id,
        'Entity': a.entity_name,
        'Value': a.value,
        'Threshold': a.threshold,
        'Message': a.message,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Alerts');
      XLSX.writeFile(wb, 'alerts_export.xlsx');
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const toggleGroup = (type) => {
    setExpandedType((prev) => (prev === type ? null : type));
  };

  if (loading) {
    return (
      <div className="alerts-analysis">
        <div className="alert-loading">
          <div className="spinner" />
          <p>Checking for anomalies...</p>
        </div>
      </div>
    );
  }

  if (!alertData) return null;

  const { alerts = [], summary = {} } = alertData;
  const hasAlerts = alerts.length > 0;

  // Group alerts by alert_type
  const grouped = {};
  alerts.forEach((a) => {
    if (!grouped[a.alert_type]) grouped[a.alert_type] = [];
    grouped[a.alert_type].push(a);
  });

  // Determine highest severity per group
  const groupSeverity = (items) => {
    return items.some((a) => a.severity === 'critical') ? 'critical' : 'warning';
  };

  return (
    <div className="alerts-analysis">
      {/* Summary Banner */}
      {!hasAlerts ? (
        <div className="alert-banner clear">
          All Clear &mdash; No anomalies detected
        </div>
      ) : (
        <div className="alert-banner has-alerts">
          {summary.critical > 0 && (
            <span className="alert-badge critical">
              {SEVERITY_ICONS.critical} {summary.critical} Critical
            </span>
          )}
          {summary.warning > 0 && (
            <span className="alert-badge warning">
              {SEVERITY_ICONS.warning} {summary.warning} Warnings
            </span>
          )}
          <div className="alert-type-chips">
            {summary.by_type && Object.entries(summary.by_type).map(([type, count]) =>
              count > 0 ? (
                <span key={type} className="alert-type-chip">
                  {TYPE_LABELS[type] || type}: {count}
                </span>
              ) : null
            )}
          </div>
        </div>
      )}

      {hasAlerts && (
        <>
          {/* Export Row */}
          <div className="alert-export-row">
            <button className="btn btn-sm" onClick={handleExport} disabled={exporting}>
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </button>
          </div>

          {/* Alert Groups */}
          <div className="alert-groups">
            {Object.entries(grouped).map(([type, items]) => {
              const isExpanded = expandedType === type;
              const severity = groupSeverity(items);

              return (
                <div key={type} className="alert-group">
                  <div
                    className="alert-group-header"
                    onClick={() => toggleGroup(type)}
                  >
                    <span className="alert-group-icon">
                      {TYPE_ICONS[type] || '\u26A0'}
                    </span>
                    <span className="alert-group-label">
                      {TYPE_LABELS[type] || type}
                    </span>
                    <span className={`alert-group-count ${severity}`}>
                      {items.length}
                    </span>
                    <span className="alert-group-toggle">
                      {isExpanded ? '\u25B2' : '\u25BC'}
                    </span>
                  </div>

                  {isExpanded && (
                    <div className="alert-table-wrapper">
                      <table className="alert-table">
                        <thead>
                          <tr>
                            <th>Severity</th>
                            <th>Load ID</th>
                            <th>Entity</th>
                            <th>Value</th>
                            <th>Threshold</th>
                            <th>Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((a, i) => (
                            <tr key={`${a.load_id}-${i}`}>
                              <td>
                                <span className={`alert-severity ${a.severity}`}>
                                  {a.severity}
                                </span>
                              </td>
                              <td className="alert-load-id">{a.load_id}</td>
                              <td>{a.entity_name}</td>
                              <td>{a.value}</td>
                              <td>{a.threshold}</td>
                              <td>{a.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
